import React from 'react'
import { LKButton } from 'loankeeper-common/components'

import getIconComponent from '../getIconComponent'
import { stateSortToAgGrid } from '../helpers'
import { useGridIdentity } from './GridIdentityProvider'
import { translateColName } from '../../helpers'
import { useCurrentGridState } from './useCurrentGridState'

function getIconGetter(iconComponent) {
  if (typeof iconComponent === 'function') {
    return getIconComponent(iconComponent)
  }

  return iconComponent
}

const ButtonContextMenu = React.memo(() => {
  return (
    <LKButton
      tabIndex="-1"
      alias="down_arrow"
      onClick={({ currentTarget }) => {
        const targetRect = currentTarget.getBoundingClientRect()

        const contextMenuEvent = new MouseEvent('contextmenu', {
          view: window,
          bubbles: true,
          cancelable: true,
          clientX: targetRect.right - targetRect.width,
          clientY: targetRect.bottom,
        })

        currentTarget.dispatchEvent(contextMenuEvent)
      }}
    />
  )
})

const INDEX_COLUMN_DEF = {
  headerName: 'X',
  suppressMenu: true,
  sortable: true,
  suppressMovable: true,
  resizable: true,
  // suppressToolPanel: true,
  suppressColumnsToolPanel: true,
  suppressFiltersToolPanel: true,
  suppressSizeToFit: true,
  lockPosition: true,
  cellRenderer: ButtonContextMenu,
  width: 30,
} as const

type ColumnDef = {
  headerName: string
  field: string
  width: number
  hide: boolean
  menuTabs: string[]
  cellRenderer: any
  filter: any
  suppressMenu?: boolean
  suppressMovable?: boolean
  resizable?: boolean
  // suppressToolPanel?: boolean
  suppressColumnsToolPanel?: boolean
  suppressFiltersToolPanel?: boolean
  suppressSizeToFit?: boolean
  cellStyle: any | undefined
  cellClass: string | undefined
  cellClassRules: any | undefined
  valueFormatter: Function | undefined
  sort?: string
  comparator?: Function
}

type UseGridColumnsAttributes = {
  allowColumnHide: boolean
  enableFilter: boolean
  iColumnDefs: any
  iColumnConfigs: any
  iGetContextMenuItems: any
  iEnableSorting: boolean
  sortedColumn: { name: string; sortState: number } | undefined
}

export const useGridColumns = ({
  allowColumnHide,
  enableFilter,
  iColumnDefs,
  iColumnConfigs,
  iGetContextMenuItems,
  iEnableSorting,
  sortedColumn,
}: UseGridColumnsAttributes) => {
  const id = useGridIdentity()

  const { orders, filters, widths, visibilities } = useCurrentGridState()

  return React.useMemo((): Array<ColumnDef | typeof INDEX_COLUMN_DEF> => {
    const [form, object] = id.split('-')
    const components: any[] = []
    const notTranslated = []

    const columnDefs: ColumnDef[] = (iColumnDefs || orders.map((order) => ({ headerName: order, field: order })))
      .filter(({ field = '' }) => !field.includes('HashSum_'))
      .map(({ headerName, field, width }) => {
        const filter = filters[field]

        const menuTabs = [allowColumnHide && 'columnsMenuTab', enableFilter && filter && 'filterMenuTab'].filter(
          Boolean,
        )

        const {
          cellRenderer,
          cellStyle,
          cellClass,
          cellClassRules,
          valueFormatter,
          comparator,
          isDisplayHeader,
          iconComponent,
        } = iColumnConfigs[field] || {}

        const cellRendererFramework = getIconGetter(iconComponent)

        let columnDef: ColumnDef = {
          headerName: translateColName(form, object, headerName),
          field,
          width: width || widths[field],
          hide: visibilities[field] === false,
          menuTabs,
          cellRenderer: cellRendererFramework || cellRenderer,
          // filter: typeof rowSample[field] === 'number' ? 'agNumberColumnFilter' : 'agTextColumnFilter',
          filter,
          cellStyle,
          cellClass,
          cellClassRules,
          valueFormatter,
        }

        if (iEnableSorting) {
          columnDef.comparator = comparator
        }

        if (cellRendererFramework != null) {
          columnDef = {
            ...columnDef,
            headerName: isDisplayHeader ? columnDef.headerName : '',
            suppressMenu: true,
            suppressMovable: true,
            resizable: !isDisplayHeader, // if Display header then should be allow to resize column
            // suppressToolPanel: true,
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true,
            suppressSizeToFit: !isDisplayHeader, // if Display header then should be allow to resize column
            cellRenderer: cellRendererFramework,
          }
        }

        if (sortedColumn && sortedColumn.name === field) {
          columnDef.sort = stateSortToAgGrid(sortedColumn.sortState)
        } else {
          columnDef.sort = stateSortToAgGrid(0)
        }

        if (columnDef.headerName === columnDef.field && form !== 'frm_creports') {
          notTranslated.push({
            form,
            object,
            headerName,
            translationKey: `${form}-${object}.${headerName}`,
          })
        }

        return columnDef
      })
    if (notTranslated.length > 0) {
      console.log('-------------needs translations--------------')
      console.table(notTranslated)
      console.log('---------------------------------------------')
    }

    if (iGetContextMenuItems == null) {
      return columnDefs
    }

    return [INDEX_COLUMN_DEF, ...columnDefs]
  }, [
    orders,
    widths,
    visibilities,
    filters,
    sortedColumn,
    allowColumnHide,
    enableFilter,
    iColumnConfigs,
    iEnableSorting,
    iGetContextMenuItems,
    id,
  ])
}
